import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useLocalization } from "@progress/kendo-react-intl";
import { useStores } from "src/stores";
import { useServices } from "src/services";
import { Button } from "@progress/kendo-react-buttons";
import moment from "moment";
// @ts-ignore
import PrivacyBanner from "react-ford-ccpa-banner";
import kitTrackingImage from "../assets/images/DFYC2026Kit_page.jpg";
import "./KitTracking.scss";

const KitTracking = observer(() => {
  const { language } = useLocalization();
  const { ui } = useStores();
  const { d4ur } = useServices();
  const [trackingNumber, setTrackingNumber] = useState<string | null>(null);
  const [secondaryTrackingNumber, setSecondaryTrackingNumber] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const [fetchedEventId, setFetchedEventId] = useState<number | null>(null);

  // Which event this page describes. Honours the header's event switcher when a
  // selection exists; otherwise falls back to the event whose kit-shipping window
  // (30 days before start, through end) covers today. Without the switcher check
  // a dealer with two events would see the wrong kit's tracking number.
  const getPageEvent = () => {
    const events = ui.events || [];

    if (ui.selectedEventID) {
      const selected = events.find(
        (event: any) => Number(event?.event_id) === Number(ui.selectedEventID)
      );
      if (selected) return selected;
    }

    const today = moment().startOf('day');
    return events.find((event: any) => {
      if (!event.start_date || !event.end_date) return false;
      const eventStartDate = moment(event.start_date).startOf('day');
      const eventEndDate = moment(event.end_date).startOf('day');
      const thirtyDaysBeforeStart = eventStartDate.clone().subtract(30, 'days');
      return today.isSameOrAfter(thirtyDaysBeforeStart) && today.isSameOrBefore(eventEndDate);
    });
  };

  const pageEvent = getPageEvent();

  // Fetch event details to get kit_tracking_number and secondary_tracking_number
  useEffect(() => {
    const event = pageEvent;

    if (event?.event_id && !loading && (!hasFetched || fetchedEventId !== event.event_id)) {
      setLoading(true);
      setFetchedEventId(event.event_id);
      d4ur.getEventDetails(event.event_id).then((response: any) => {
        const eventData = response?.result?.event;
        setTrackingNumber(eventData?.kit_tracking_number ?? null);
        setSecondaryTrackingNumber(eventData?.secondary_tracking_number ?? null);
        setHasFetched(true);
        setLoading(false);
      }).catch((error: any) => {
        console.error('Error fetching event details:', error);
        setHasFetched(true);
        setLoading(false);
      });
    }
  }, [ui.events, ui.selectedEventID, d4ur, loading, hasFetched, fetchedEventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleKitTracking = () => {
    if (trackingNumber) {
      window.open(`https://www.fedex.com/apps/fedextrack/?tracknumbers=${trackingNumber}`, "_blank");
    }
  };

  const handleSecondaryKitTracking = () => {
    if (secondaryTrackingNumber) {
      window.open(`https://www.fedex.com/apps/fedextrack/?tracknumbers=${secondaryTrackingNumber}`, "_blank");
    }
  };

  return (
    <>
      <div className="kit-tracking-page main-content">
        <div className="kit-tracking-container">
          <h1 className="kit-tracking-title">Kit Details and Tracking</h1>
          {pageEvent?.charity_partner && (
            <h2 className="kit-tracking-charity">{pageEvent.charity_partner}</h2>
          )}
          <p className="kit-tracking-description">
            Your event kit ships about three weeks before your event. Track your shipment here so you know exactly when it will arrive.
          </p>

          <div className="kit-items-grid">
            <div className="kit-item">
              <img src={kitTrackingImage} alt="Kit items" className="kit-image" />
            </div>
          </div>

          <div className="tracking-section">
            <div className="link-section">
              <div className="link-label">Kit Tracking</div>
              <Button
                className="tracking-button"
                onClick={handleKitTracking}
                primary={true}
                disabled={!trackingNumber}
              >
                Link to Kit Tracking
              </Button>
            </div>
          </div>

          {secondaryTrackingNumber && (
            <div className="tracking-section">
              <div className="link-section">
                <div className="link-label">Secondary Kit Tracking</div>
                <Button
                  className="tracking-button"
                  onClick={handleSecondaryKitTracking}
                  primary={true}
                >
                  Link to Secondary Kit Tracking
                </Button>
              </div>
            </div>
          )}

          <div className="spanish-kit-notice">
            If you wish to receive a Spanish Language Event Kit, please let PHQ know at{" "}
            <a href="mailto:hello@driveforyourcommunity.com" className="email-link">hello@driveforyourcommunity.com</a>
          </div>
        </div>
      </div>
      <PrivacyBanner
        containerStyle={{ padding: "10px 0 0", width: "100%" }}
        privacyContainerStyle={{
          padding: "10px 0",
          width: "60%",
          marginLeft: "20%",
        }}
        showPrivacy={true}
        language={language}
        team={"ford"}
        imgStyle={{ paddingLeft: "30px" }}
        otherLinks={[
          { text: "CONTACT US", url: "/contact", target: "_blank" },
          { text: "PROGRAM TERMS AND CONDITIONS", url: "/terms-eligibility", target: "_blank" },
          { text: "PROGRAM RESOURCES", url: "/resources", target: "_blank" },
        ]}
      />
    </>
  );
});

export default KitTracking;

